import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl, { LngLatLike } from 'mapbox-gl';
import { lineString, Feature, LineString, Position } from '@turf/helpers';
import turfLength from '@turf/length';
import { COLOR_RED_BASE } from '@alltrails/shared/denali/tokens';
import useAnimationFrame from 'hooks/useAnimationFrame';
import useFeatures from 'hooks/useFeatures';
import { MapboxMapShim } from 'types/Map';
import { computePositionValues, initializeFlyoverMap } from 'utils/flyoverHelpers';
import { atMapsToGeojson } from 'utils/mapbox/sources/at_maps';
import useMapLoadedEvents from './useMapLoadedEvents';

const ANIMATION_DURATION_MS = 40000;

export type FlyoverLoadingState = 'notStarted' | 'loading' | 'done';

export type FlyoverSettings = {
  didMapLoadEventsFire: boolean;
  flyoverLoadingState: FlyoverLoadingState;
  initFlyover: (terrainActive: boolean, enable3D: () => void) => void;
  isPaused: boolean;
  resetAnimation: () => void;
  setAnimationDuration: (newDuration: number) => void;
  toggleCameraLock: () => void;
  togglePauseFlyover: () => void;
};

export default function useFlyover(map: MapboxMapShim, atMap: any): FlyoverSettings {
  const [flyoverLoadingState, setFlyoverLoadingState] = useState<FlyoverLoadingState>('notStarted');

  // map references
  const popup = useRef<mapboxgl.Popup>(null);
  const marker = useRef<mapboxgl.Marker>(null);

  // route info
  const atMapGeojson = useMemo(() => atMapsToGeojson([atMap]), [atMap]);
  const pinRoute = useMemo(() => atMapGeojson.features[0]?.geometry.coordinates as Position[][], [atMapGeojson]);
  const [path, setPath] = useState<
    Feature<
      LineString,
      {
        [name: string]: any;
      }
    >
  >();
  const [pathDistance, setPathDistance] = useState<number>();

  // animation state
  const animationPhase = useRef(0);
  const lockCamera = useRef(false);
  const prevCoordinates = useRef(null);
  const startTime = useRef(0);
  const shouldResetTime = useRef(false);
  const timeElapsed = useRef(0);
  const [speedFactor, setSpeedFactor] = useState(1);
  const [isPaused, setIsPaused] = useState(true);

  const features = useFeatures();
  const { didMapLoadEventsFire } = useMapLoadedEvents(map);

  useEffect(() => {
    if (features.includes('trail_flyover') && pinRoute && pinRoute[0].length > 1) {
      const pathString = lineString(pinRoute[0]);
      setPath(pathString);
      setPathDistance(turfLength(pathString));
    }
  }, [features, pinRoute]);

  // responsible for updating the animation state on each call to `requestAnimationFrame`
  const flyoverFrame = (time: DOMHighResTimeStamp) => {
    if (shouldResetTime.current) {
      startTime.current = time - timeElapsed.current;
      shouldResetTime.current = false;
    } else {
      timeElapsed.current = time - startTime.current;
    }

    // animationPhase is a value between 0 and 1 that represents the progress of the animation
    animationPhase.current = (timeElapsed.current / ANIMATION_DURATION_MS) * speedFactor;
    if (animationPhase.current > 1) {
      // stop the animation
      setIsPaused(true);
      return;
    }

    const { bearing, elevation, lngLat, position } = computePositionValues(
      map,
      path,
      pathDistance,
      animationPhase.current /* percentageComplete */,
      prevCoordinates.current
    );

    // Update the popup altitude value and marker location
    popup.current.setHTML(`Altitude: ${elevation} m<br/>`);
    marker.current.setLngLat(lngLat);

    // Reduce the visible length of the line by using a line-gradient to cutoff the line
    map.setPaintProperty('line', 'line-gradient', ['step', ['line-progress'], COLOR_RED_BASE, animationPhase.current, 'rgba(255, 0, 0, 0)']);

    /* Camera logic */
    if (lockCamera.current) {
      const camera = map.getFreeCameraOptions();

      // tell the camera to look at a point along the route
      camera.lookAtPoint(position);

      map.setFreeCameraOptions(camera);
    } else {
      map.easeTo({
        center: position,
        bearing: bearing ?? 10, // default to a 10 degree bearing at the start (subject to change)
        speed: 0.7
      });
    }

    prevCoordinates.current = position;
  };

  // memoize to avoid extra calls to `cancelAnimationFrame` due to re-rendering of the compass rotation
  const animationDeps = useMemo(() => [speedFactor], [speedFactor]);
  useAnimationFrame(flyoverFrame, isPaused, animationDeps);

  /**
   * Initialize flyover by
   * 1) Enabling 3D
   * 2) Removing the existing polyline
   * 3) Drawing the line and marker that will be animated
   */
  const initFlyover = useCallback(
    (terrainActive: boolean, enable3D: () => void) => {
      if (!features.includes('trail_flyover') || !map || !atMap || !didMapLoadEventsFire) {
        return;
      }

      setFlyoverLoadingState('loading');

      // TODO: check for pro
      if (!terrainActive) {
        enable3D();
      }

      map.once('idle', () => {
        initializeFlyoverMap(map, atMapGeojson);

        popup.current = new mapboxgl.Popup({ closeButton: false });
        marker.current = new mapboxgl.Marker({
          color: 'red',
          scale: 0.8,
          draggable: false,
          pitchAlignment: 'auto',
          rotationAlignment: 'auto'
        })
          .setLngLat(pinRoute[0][0] as LngLatLike)
          .setPopup(popup.current)
          .addTo(map)
          .togglePopup();

        map.flyTo({
          center: pinRoute[0][0] as LngLatLike,
          zoom: 14,
          bearing: 10, // TODO: update initial bearing
          speed: 0.3,
          essential: true
        });

        // example of how we can pause animations from user map interactions
        map.on('click', () => {
          setIsPaused(true);
        });

        setFlyoverLoadingState('done');
      });
    },
    [features, map, atMap, didMapLoadEventsFire, atMapGeojson, pinRoute]
  );

  /**
   * Changes the speed factor of the playback by setting a the speed factor as a multiplier of it's position.
   * @param newSpeed new speed multiplier value
   */
  const changeSpeedFactor = (newSpeed: number) => {
    setSpeedFactor(newSpeed);
  };

  /**
   * Pause and reset the animation, resetting the marker and polyline.
   */
  const resetAnimation = () => {
    setIsPaused(true);
    animationPhase.current = 0;
    timeElapsed.current = 0;
    startTime.current = 0;
    marker.current.setLngLat(pinRoute[0][0] as LngLatLike);
    map.setPaintProperty('line', 'line-gradient', ['step', ['line-progress'], 'red', 0, 'rgba(255, 0, 0, 0)']);
  };

  /**
   * Toggles the camera auto-pan.
   */
  const toggleCameraLock = () => {
    setIsPaused(true);
    lockCamera.current = !lockCamera.current;
  };

  /**
   * Toggles the animation play state.
   */
  const togglePauseFlyover = () => {
    if (isPaused) {
      shouldResetTime.current = true;
    }

    setIsPaused(paused => !paused);
  };

  // TODO: TRAILS-692 exit flyover

  return {
    didMapLoadEventsFire,
    flyoverLoadingState,
    initFlyover,
    isPaused,
    resetAnimation,
    setAnimationDuration: changeSpeedFactor,
    toggleCameraLock,
    togglePauseFlyover
  };
}
